import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import useAxios from "../../../../hooks/useAxios";
import "./DeleteStudentConfirmationModal.css";
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';

const DeleteStudentConfirmationModal = ({ open, onClose, student, onStudentDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const axiosInstance = useAxios();

  const handleDelete = async () => {
    if (!student) return;

    setError(null);
    setLoading(true);

    try {
      const response = await axiosInstance.delete(`/student-accounts/${student.id}`);

      if (response.data.success) {
        // Call the callback to refresh the table
        if (onStudentDeleted) {
          onStudentDeleted();
        }
        onClose();
      } else {
        setError(response.data.message || "Failed to delete student account.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!student) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="modal-title">
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          Delete Student Account
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography className="modal-description">
          Are you sure you want to delete the student account for <strong>{student.student_profile?.full_name || 'Unknown'}</strong>?
        </Typography>

        <Alert severity="warning" className="warning-alert">
          <Typography variant="body2">
            <strong>Warning:</strong> This action cannot be undone. The student will lose access to their account and all associated data will be permanently deleted.
          </Typography>
        </Alert>

        <Box className="student-details">
          <Typography variant="body2" className="detail-item">
            <strong>Student Name:</strong> {student.student_profile?.full_name || 'N/A'}
          </Typography>
          <Typography variant="body2" className="detail-item">
            <strong>Login Code:</strong> {student.login_code || 'N/A'}
          </Typography>
          {/* <Typography variant="body2" className="detail-item">
            <strong>Email:</strong> {student.email || 'N/A'}
          </Typography> */}
          <Typography variant="body2" className="detail-item">
            <strong>Created:</strong> {new Date(student.created_at).toLocaleDateString()}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" className="error-alert">
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions className="modal-actions">
        <Button 
          onClick={handleClose} 
          disabled={loading}
          className="secondary-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          className="delete-button"
          variant="contained"
        >
          {loading ? "Deleting..." : "Delete Student"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteStudentConfirmationModal; 